import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { fmtSwedishDate } from "@/lib/api";
import { Reveal } from "@/components/invite/Reveal";
import { Rsvp } from "@/components/invite/Rsvp";
import { FloralBranch, OrnamentDivider } from "@/components/Floral";
import ulvaKvarn from "@/assets/ulva-kvarn.jpg";

const Section = ({ id, children, className = "" }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
    className={`relative px-6 py-20 sm:py-28 ${className}`}
  >
    <div className="max-w-xl mx-auto">{children}</div>
  </motion.section>
);

const InfoRow = ({ label, value }) => (
  <div className="text-center">
    <p className="label-caps text-[0.65rem]" style={{ color: "var(--gold)" }}>
      {label}
    </p>
    <p className="font-serif-d text-2xl mt-1.5" style={{ color: "var(--ink)" }}>
      {value}
    </p>
  </div>
);

const FaqItem = ({ faq, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid rgba(212,185,160,0.5)" }}>
      <button
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
        onClick={() => setOpen(!open)}
        data-testid={`faq-item-${index}`}
      >
        <span className="font-serif-d text-xl" style={{ color: "var(--ink)" }}>
          {faq.q}
        </span>
        <Plus
          className="w-4 h-4 shrink-0 transition-transform duration-500"
          style={{ color: "var(--gold)", transform: open ? "rotate(45deg)" : "none" }}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="font-serif-d italic text-lg pb-6 pr-8 leading-relaxed" style={{ color: "var(--ink-soft)" }}>
              {faq.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Invitation() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        if (token) {
          const { data } = await api.get(`/invite/${token}`);
          setData(data);
        } else {
          const { data } = await api.get("/settings");
          setData({ settings: data, guest: null });
        }
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      }
    };
    load();
  }, [token]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <p className="label-caps text-sm" style={{ color: "var(--ink)" }}>
            Inbjudan hittades inte
          </p>
          <p className="font-serif-d italic text-lg mt-4" style={{ color: "var(--ink-soft)" }}>
            Kontrollera länken du fått, eller hör av dig till oss.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen" />;

  const { settings, guest } = data;
  const greeting = guest
    ? guest.plus_one_name
      ? `Kära ${guest.name} & ${guest.plus_one_name}`
      : `Kära ${guest.name}`
    : null;

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <AnimatePresence>
        {!opened && <Reveal settings={settings} guestName={greeting} onOpen={() => setOpened(true)} />}
      </AnimatePresence>

      {opened && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2 }}>
          {/* Minimal nav */}
          <nav
            className="fixed top-0 inset-x-0 z-40 backdrop-blur-md"
            style={{ backgroundColor: "rgba(249,246,240,0.82)", borderBottom: "1px solid rgba(212,185,160,0.35)" }}
            data-testid="main-nav"
          >
            <div className="max-w-xl mx-auto flex items-center justify-center gap-5 sm:gap-8 py-3.5 label-caps text-[0.6rem] sm:text-[0.65rem]">
              <a href="#invitation" className="hover:opacity-60 transition-opacity" data-testid="nav-invitation">
                Inbjudan
              </a>
              <a
                href="#rsvp"
                className="px-3 py-1 hover:opacity-70 transition-opacity"
                style={{ border: "1px solid var(--ink)" }}
                data-testid="nav-rsvp"
              >
                OSA
              </a>
              <a href="#day" className="hover:opacity-60 transition-opacity" data-testid="nav-day">
                Dagen
              </a>
              <a href="#location" className="hover:opacity-60 transition-opacity" data-testid="nav-location">
                Plats
              </a>
            </div>
          </nav>

          {/* Hero / invitation */}
          <Section id="invitation" className="min-h-[100svh] flex items-center justify-center !py-16 overflow-hidden">
            <FloralBranch className="absolute top-16 -left-8 w-44 sm:w-56 opacity-70 pointer-events-none" />
            <FloralBranch flip className="absolute bottom-4 -right-8 w-44 sm:w-56 opacity-70 pointer-events-none" />
            <div className="relative text-center w-full pt-6">
              <p className="label-caps text-[0.65rem]" style={{ color: "var(--gold)" }}>
                Tillsammans med våra familjer
              </p>
              <h1 className="font-script script-display text-7xl sm:text-8xl mt-8 leading-none" style={{ color: "var(--ink)" }}>
                {settings.couple_name_1}
              </h1>
              <p className="font-serif-d italic text-3xl my-3" style={{ color: "var(--gold)" }}>
                &amp;
              </p>
              <h1 className="font-script script-display text-7xl sm:text-8xl leading-none" style={{ color: "var(--ink)" }}>
                {settings.couple_name_2}
              </h1>
              <OrnamentDivider className="my-10" />
              <p className="label-caps text-xs sm:text-sm" style={{ color: "var(--ink)" }}>
                {settings.wedding_date} · {settings.venue_name}
              </p>
              {greeting && (
                <div className="mt-14 max-w-md mx-auto" data-testid="personal-greeting">
                  <p className="font-script text-4xl" style={{ color: "var(--ink)" }}>
                    {greeting},
                  </p>
                  <p className="font-serif-d italic text-xl mt-4 leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                    Vi önskar inget hellre än att få fira denna dag tillsammans med er.
                  </p>
                </div>
              )}
            </div>
          </Section>

          {/* RSVP */}
          <Section id="rsvp">
            <FloralBranch className="absolute -top-2 -right-10 w-40 opacity-60 pointer-events-none rotate-180" />
            <p className="label-caps text-xs text-center mb-4" style={{ color: "var(--ink)" }}>
              OSA
            </p>
            {guest ? (
              <>
                <p className="font-serif-d italic text-2xl text-center mb-4" style={{ color: "var(--ink-soft)" }}>
                  Vill ni fira med oss?
                </p>
                {settings.rsvp_deadline && (
                  <p className="label-caps text-[0.6rem] text-center mb-12" style={{ color: "var(--gold)" }} data-testid="rsvp-deadline">
                    Svara senast {fmtSwedishDate(settings.rsvp_deadline)}
                  </p>
                )}
                {!settings.rsvp_deadline && <div className="mb-8" />}
                <Rsvp token={token} guest={guest} settings={settings} onUpdated={(g) => setData({ settings, guest: g })} />
              </>
            ) : (
              <p className="font-serif-d italic text-xl text-center" style={{ color: "var(--ink-soft)" }} data-testid="rsvp-no-token">
                Din personliga OSA-länk finns i inbjudan du fått av oss.
              </p>
            )}
          </Section>

          <OrnamentDivider />

          {/* The day */}
          <Section id="day">
            <p className="label-caps text-xs text-center mb-12" style={{ color: "var(--ink)" }}>
              Dagen
            </p>
            <div className="space-y-10">
              <InfoRow label="Datum" value={settings.wedding_date} />
              <InfoRow label="Vigsel" value={`kl. ${settings.ceremony_time}`} />
              <InfoRow label="Plats" value={settings.venue_name} />
              {settings.dress_code && <InfoRow label="Klädkod" value={settings.dress_code} />}
            </div>
            {settings.notice && (
              <div className="mt-14 text-center" data-testid="wedding-notice">
                <div className="hairline w-16 mx-auto mb-7" />
                {settings.notice.split("\n").map((line, i) => (
                  <p key={i} className="font-serif-d italic text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                    {line}
                  </p>
                ))}
              </div>
            )}
            {settings.rsvp_deadline && (
              <p className="font-serif-d italic text-center text-lg mt-12" style={{ color: "var(--ink-soft)" }}>
                Vänligen OSA senast {fmtSwedishDate(settings.rsvp_deadline)}
              </p>
            )}
          </Section>

          <OrnamentDivider />

          {/* Location */}
          <Section id="location">
            <p className="label-caps text-xs text-center mb-10" style={{ color: "var(--ink)" }}>
              Plats
            </p>
            <div className="text-center">
              <motion.img
                src={ulvaKvarn}
                alt="Ulva Kvarn — illustration"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
                className="w-64 sm:w-80 mx-auto mb-10 mix-blend-multiply"
                style={{ opacity: 0.9 }}
                data-testid="venue-illustration"
              />
              <p className="font-script text-5xl" style={{ color: "var(--ink)" }}>
                {settings.venue_name}
              </p>
              <p className="font-serif-d text-xl mt-5" style={{ color: "var(--ink-soft)" }}>
                {settings.venue_address}
              </p>
              <a
                className="btn-invite mt-10 no-underline"
                href={`https://maps.google.com/?q=${encodeURIComponent(settings.maps_query || settings.venue_address)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-maps-btn"
              >
                Öppna i kartor
              </a>
            </div>
          </Section>

          <OrnamentDivider />

          {/* FAQ */}
          {settings.faqs?.length > 0 && (
            <>
              <Section id="faq">
                <p className="label-caps text-xs text-center mb-10" style={{ color: "var(--ink)" }}>
                  Frågor &amp; svar
                </p>
                <div style={{ borderTop: "1px solid rgba(212,185,160,0.5)" }}>
                  {settings.faqs.map((f, i) => (
                    <FaqItem key={i} faq={f} index={i} />
                  ))}
                </div>
              </Section>
            </>
          )}

          <footer className="text-center pb-16 px-6">
            <div className="hairline w-24 mx-auto mb-8" />
            <p className="font-script text-3xl" style={{ color: "var(--gold)" }}>
              {settings.couple_name_1} &amp; {settings.couple_name_2}
            </p>
            <p className="label-caps text-[0.6rem] mt-3" style={{ color: "var(--ink-soft)" }}>
              {settings.wedding_date}
            </p>
          </footer>
        </motion.div>
      )}
    </div>
  );
}
